import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Heart, MapPin, Loader2 } from "lucide-react";
import Footer from "../components/Footer";
import SafeImage from "../components/SafeImage";
import BookmarkHeart from "../components/BookmarkHeart";
import { useAuth } from "../context/AuthContext";

/**
 * /saved — the user's curated wishlist of tours and cities they've hearted.
 * Two horizontal-stacked sections (tours, cities), each with quick links
 * back to detail pages. Built from a single /bookmarks fetch which returns
 * (kind, slug, created_at) rows; we hydrate them client-side by joining
 * against the public /featured-routes and /cities catalogues.
 */
export default function Saved() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);   // [{kind, slug, created_at}]
  const [tours, setTours] = useState([]);     // catalog rows
  const [cities, setCities] = useState([]);   // catalog rows

  useEffect(() => {
    Promise.all([
      api.get("/bookmarks"),
      api.get("/featured-routes"),
      api.get("/cities"),
    ])
      .then(([b, t, c]) => {
        setItems(b.data.items || []);
        setTours(t.data || []);
        setCities(c.data || []);
      })
      .catch(() => {
        // ProtectedRoute will redirect anon users; render empty for non-401s.
        setItems([]);
        setTours([]);
        setCities([]);
      });
  }, []);

  if (items === null) {
    return (
      <div className="pt-32 text-center"><Loader2 className="h-8 w-8 text-gold mx-auto animate-spin" /></div>
    );
  }

  const tourSlugs = new Set(items.filter((i) => i.kind === "tour").map((i) => i.slug));
  const citySlugs = new Set(items.filter((i) => i.kind === "city").map((i) => i.slug));
  const savedTours = tours.filter((t) => tourSlugs.has(t.slug));
  const savedCities = cities.filter((c) => citySlugs.has(c.slug));

  return (
    <div data-testid="saved-page" className="pt-safe-navbar-lg pb-16">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="mb-10">
          <div className="iq-overline mb-2 flex items-center gap-2">
            <Heart className="h-3.5 w-3.5" /> Your saved
          </div>
          <h1 className="font-display text-cream text-5xl md:text-6xl tracking-wider">Saved journeys</h1>
          <p className="font-serif italic text-cream-dim text-lg mt-2">
            {savedTours.length + savedCities.length === 0
              ? "Heart any tour or city to build your wishlist — they'll appear here."
              : `${savedTours.length + savedCities.length} pinned for later, ${user?.name?.split(" ")[0] || "friend"}.`}
          </p>
        </div>

        {/* Saved tours */}
        <section className="mb-12">
          <div className="iq-overline mb-4">Tours</div>
          {savedTours.length === 0 ? (
            <EmptyHint label="tours" cta="Browse tours" to="/routes" />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="saved-tours">
              {savedTours.map((r) => (
                <Link
                  key={r.slug}
                  to={`/routes/${r.slug}`}
                  data-testid={`saved-tour-${r.slug}`}
                  className="iq-card overflow-hidden group block"
                >
                  <div className="relative aspect-[16/9]">
                    <SafeImage src={r.hero} alt={r.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-transparent" />
                    <div className="absolute top-3 right-3">
                      <BookmarkHeart kind="tour" slug={r.slug} label={r.name} />
                    </div>
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="font-display text-cream text-2xl tracking-wider leading-none">{r.name}</h3>
                      <p className="font-serif italic text-cream-dim text-sm mt-1">{r.tagline}</p>
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between text-xs text-cream-dim">
                    <span>{r.cities?.length || 0} cities · {r.duration_days || "—"}d</span>
                    <span className="text-gold inline-flex items-center gap-1">View <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Saved cities */}
        <section>
          <div className="iq-overline mb-4">Cities</div>
          {savedCities.length === 0 ? (
            <EmptyHint label="cities" cta="Open the atlas" to="/map" />
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="saved-cities">
              {savedCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/city/${c.slug}`}
                  data-testid={`saved-city-${c.slug}`}
                  className="iq-card overflow-hidden group block"
                >
                  <div className="relative aspect-[4/3]">
                    <SafeImage src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-transparent" />
                    <div className="absolute top-2 right-2">
                      <BookmarkHeart kind="city" slug={c.slug} label={c.name} size="sm" />
                    </div>
                    <div className="absolute bottom-2 left-3 right-3">
                      <div className="text-[10px] text-cream-dim tracking-widest uppercase">{c.country}</div>
                      <h4 className="font-display text-cream text-xl tracking-wider leading-none">{c.name}</h4>
                    </div>
                  </div>
                  <div className="px-3 py-2 text-[11px] text-cream-dim inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-gold" /> {c.station_name}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}

function EmptyHint({ label, cta, to }) {
  return (
    <div className="iq-card p-8 text-center">
      <Heart className="h-8 w-8 text-gold/40 mx-auto mb-3" />
      <p className="font-serif italic text-cream-dim mb-4">No saved {label} yet.</p>
      <Link to={to} className="iq-btn-secondary inline-flex">{cta} <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
}
